import { App } from "@capacitor/app";
import { toError } from "@clipboard-health/util-ts";
import { getFirebaseSingleton } from "@lib/firebase";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export const useAppStateListener = () => {
  const queryClient = useQueryClient();
  const { setIsAppActive } = useAppStateContext();

  useEffect(() => {
    const result = App.addListener("appStateChange", async ({ isActive }) => {
      logEvent(APP_V2_APP_EVENTS.APP_STATE_CHANGE, {
        isActive,
      });
      setIsAppActive(isActive);
      if (!isActive) {
        return;
      }

      const firebaseUser = getFirebaseSingleton().currentUser;
      if (firebaseUser) {
        await firebaseUser.getIdToken();
      }

      // Also refresh Cognito token if available
      try {
        const cognitoUser = await getCognitoSingleton().getCurrentUserInfo();
        // Refresh token if user exists
        if (cognitoUser) {
          await getCognitoSingleton().getIdToken();
        }
      } catch (maybeError: unknown) {
        // Cognito token refresh failure is not critical, continue with app restoration
        logError(APP_V2_APP_EVENTS.FAILED_TO_REFRESH_COGNITO_TOKEN, {
          error: toError(maybeError),
        });
      }

      logEvent(APP_V2_APP_EVENTS.APP_STATE_CHANGE_INVALIDATE_GET_WORKER_QUERY);
      await invalidateGetWorkerQuery(queryClient);

      logEvent(APP_V2_APP_EVENTS.APP_RESTORED, {
        isAuthorizedInFirebase: Boolean(firebaseUser),
      });
    }).catch((maybeError: unknown) => {
      const error = toError(maybeError);

      logError(APP_V2_APP_EVENTS.FAILED_TO_SET_UP_APP_STATE_LISTENER, {
        error,
      });
    });

    return () => {
      result?.then((listener) => listener?.remove());
    };
  }, [queryClient, setIsAppActive]);
};
